<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  x: { type: Number, required: true },
  y: { type: Number, required: true },
  columns: { type: Array, default: () => [] },
  currentColumnId: { type: [Number, String], default: null },
});

const emit = defineEmits(['open', 'note', 'move', 'attend', 'delete', 'close']);

const menuRef = ref(null);

// Mantem o menu dentro da janela quando aberto perto da borda.
const style = ref({ left: `${props.x}px`, top: `${props.y}px` });

const onClickOutside = event => {
  if (!menuRef.value?.contains(event.target)) emit('close');
};

onMounted(() => {
  // Registra no proximo ciclo: o proprio contextmenu que abriu este menu ainda
  // esta propagando e fecharia o menu imediatamente.
  setTimeout(() => {
    document.addEventListener('click', onClickOutside);
    document.addEventListener('contextmenu', onClickOutside);
  }, 0);
  const rect = menuRef.value?.getBoundingClientRect();
  if (!rect) return;
  const left = Math.min(props.x, window.innerWidth - rect.width - 8);
  const top = Math.min(props.y, window.innerHeight - rect.height - 8);
  style.value = {
    left: `${Math.max(8, left)}px`,
    top: `${Math.max(8, top)}px`,
  };
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside);
  document.removeEventListener('contextmenu', onClickOutside);
});
</script>

<template>
  <div
    ref="menuRef"
    class="fixed z-50 min-w-52 rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
    :style="style"
  >
    <button
      type="button"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2"
      @click="emit('open')"
    >
      <Icon icon="i-lucide-panel-right-open" class="size-3.5" />
      {{ $t('KANBAN.OPEN_DETAILS') }}
    </button>
    <button
      type="button"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2"
      @click="emit('note')"
    >
      <Icon icon="i-lucide-sticky-note" class="size-3.5" />
      {{ $t('KANBAN.ADD_NOTE') }}
    </button>
    <button
      type="button"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2"
      @click="emit('attend')"
    >
      <Icon icon="i-lucide-message-circle" class="size-3.5" />
      {{ $t('KANBAN.ATTEND') }}
    </button>

    <button
      type="button"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm text-n-ruby-11 hover:bg-n-alpha-2"
      @click="emit('delete')"
    >
      <Icon icon="i-lucide-trash-2" class="size-3.5" />
      {{ $t('KANBAN.DELETE_CARD') }}
    </button>

    <div class="my-1 h-px bg-n-weak" />
    <p class="mb-0 px-3 py-1 text-xxs uppercase tracking-wide text-n-slate-10">
      {{ $t('KANBAN.MOVE_TO') }}
    </p>
    <button
      v-for="column in columns"
      :key="column.id"
      type="button"
      class="flex w-full items-center gap-2 px-3 py-1.5 text-start text-sm hover:bg-n-alpha-2 disabled:opacity-40"
      :disabled="column.id === currentColumnId"
      @click="emit('move', column.id)"
    >
      <span
        class="size-2 shrink-0 rounded-full"
        :style="{ backgroundColor: column.color }"
      />
      <span class="truncate text-n-slate-12">
        {{ column.name || $t('KANBAN.UNASSIGNED_COLUMN') }}
      </span>
    </button>
  </div>
</template>
